import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';
import {
  OrgStack,
  OrgStackEntry,
  StackCategory,
  StackProvider,
  emptyStack,
} from '@/types/api/ceoxStack';

function mapRow(row: any): OrgStackEntry {
  return {
    id: row.id,
    organizationId: row.organization_id,
    category: row.category,
    provider: row.provider,
    isActive: row.is_active,
    configuredAt: row.configured_at,
  };
}

const ceoxOrgStackService = {
  async getOrgStack(): Promise<OrgStack> {
    const orgId = getActiveOrgId();
    if (!orgId) return emptyStack();

    const { data, error } = await supabase
      .from('ceox_org_stack')
      .select('*')
      .eq('organization_id', orgId);

    if (error) throw error;

    const stack = emptyStack();
    for (const row of data ?? []) {
      const entry = mapRow(row);
      stack[entry.category] = entry;
    }
    return stack;
  },

  async hasStack(): Promise<boolean> {
    const orgId = getActiveOrgId();
    if (!orgId) return false;

    const { data, error } = await supabase
      .from('ceox_org_stack')
      .select('id')
      .eq('organization_id', orgId)
      .limit(1);

    if (error) throw error;
    return (data ?? []).length > 0;
  },

  async saveStack(entries: { category: StackCategory; provider: StackProvider }[]): Promise<void> {
    const orgId = getActiveOrgId();
    if (!orgId) throw new Error('No active organization');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const rows = entries.map((e) => ({
      organization_id: orgId,
      category: e.category,
      provider: e.provider,
      is_active: true,
      configured_at: new Date().toISOString(),
      configured_by: user?.id ?? null,
    }));

    const { error } = await supabase
      .from('ceox_org_stack')
      .upsert(rows, { onConflict: 'organization_id,category' });

    if (error) throw error;
  },

  async updateProvider(category: StackCategory, provider: StackProvider): Promise<void> {
    const orgId = getActiveOrgId();
    if (!orgId) throw new Error('No active organization');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('ceox_org_stack')
      .upsert(
        {
          organization_id: orgId,
          category,
          provider,
          is_active: true,
          configured_at: new Date().toISOString(),
          configured_by: user?.id ?? null,
        },
        { onConflict: 'organization_id,category' }
      );

    if (error) throw error;
  },
};

export default ceoxOrgStackService;
